const JSON_HEADERS = {
  "Content-Type": "application/json",
};

async function adminRequest(url, options = {}) {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      ...JSON_HEADERS,
      ...(options.headers || {}),
    },
    ...options,
  });

  let data = null;
  try {
    data = await response.json();
  } catch (_) {
    data = null;
  }

  if (!response.ok) {
    const error = new Error(
      data?.error || data?.message || `Request failed with status ${response.status}`
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

export function adminLogin(email, password) {
  return adminRequest("/api/admin/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export function adminLogout() {
  return adminRequest("/api/admin/logout", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function adminCheckSession() {
  return adminRequest("/api/admin/check-session", {
    method: "GET",
  });
}

export function adminGetUserByWhatsapp(
  whatsapp_number,
  client_whatsapp_number = ""
) {
  return adminRequest("/api/admin/user/by-whatsapp", {
    method: "POST",
    body: JSON.stringify({ whatsapp_number, client_whatsapp_number }),
  });
}

export function adminGetUserByEmail(email, user_type = "public_user") {
  return adminRequest("/api/admin/user/by-email", {
    method: "POST",
    body: JSON.stringify({ email, user_type }),
  });
}

export function adminGetUserByUserId(user_id) {
  return adminRequest("/api/admin/user/by-user-id", {
    method: "POST",
    body: JSON.stringify({ user_id }),
  });
}

export function adminAdjustCredit(identityOrWhatsapp, delta, client_whatsapp_number = "") {
  const payload =
    typeof identityOrWhatsapp === "object"
      ? identityOrWhatsapp
      : { whatsapp_number: identityOrWhatsapp, delta, client_whatsapp_number };

  return adminRequest("/api/admin/user/adjust-credit", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminUpdateUserStatus(
  identityOrWhatsapp,
  user_status,
  client_whatsapp_number = ""
) {
  const payload =
    typeof identityOrWhatsapp === "object"
      ? { ...identityOrWhatsapp, user_status }
      : {
          whatsapp_number: identityOrWhatsapp,
          user_status,
          client_whatsapp_number,
        };

  return adminRequest("/api/admin/user/update-status", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminUpdateReminderFlags(
  identityOrWhatsapp,
  payload,
  client_whatsapp_number = ""
) {
  const requestPayload =
    typeof identityOrWhatsapp === "object"
      ? { ...identityOrWhatsapp, ...payload }
      : {
          whatsapp_number: identityOrWhatsapp,
          client_whatsapp_number,
          ...payload,
        };

  return adminRequest("/api/admin/user/update-reminder-flags", {
    method: "POST",
    body: JSON.stringify(requestPayload),
  });
}

export function adminSetOverride(identityOrWhatsapp, is_active, note = "") {
  const payload =
    typeof identityOrWhatsapp === "object"
      ? { ...identityOrWhatsapp, is_active, note }
      : { whatsapp_number: identityOrWhatsapp, is_active, note };

  return adminRequest("/api/admin/override/set", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminGetOverride(identityOrWhatsapp) {
  const payload =
    typeof identityOrWhatsapp === "object"
      ? identityOrWhatsapp
      : { whatsapp_number: identityOrWhatsapp };

  return adminRequest("/api/admin/override/get", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminGetChatMessages(whatsapp_number, limit = 100) {
  return adminRequest("/api/admin/chat/messages", {
    method: "POST",
    body: JSON.stringify({ whatsapp_number, limit }),
  });
}

export function adminSendChatMessage(
  from_whatsapp_number,
  to_whatsapp_number,
  message_text
) {
  return adminRequest("/api/admin/chat/send", {
    method: "POST",
    body: JSON.stringify({
      from_whatsapp_number,
      to_whatsapp_number,
      message_text,
    }),
  });
}

export function adminSendReopenTemplate(
  from_whatsapp_number,
  to_whatsapp_number
) {
  return adminRequest("/api/admin/chat/send-reopen-template", {
    method: "POST",
    body: JSON.stringify({
      from_whatsapp_number,
      to_whatsapp_number,
    }),
  });
}

export function adminOpenChat(
  whatsapp_number,
  client_whatsapp_number = ""
) {
  return adminRequest("/api/admin/chat/open", {
    method: "POST",
    body: JSON.stringify({ whatsapp_number, client_whatsapp_number }),
  });
}

export function adminGetAssistantConversations({
  limit = 100,
  intent,
  escalate,
  environment,
} = {}) {
  const params = new URLSearchParams();
  if (limit) params.set("limit", limit);
  if (intent) params.set("intent", intent);
  if (escalate !== undefined && escalate !== null && escalate !== "") {
    params.set("escalate", escalate);
  }
  if (environment) params.set("environment", environment);

  const query = params.toString();
  return adminRequest(
    `/api/admin/assistant-conversations${query ? `?${query}` : ""}`,
    {
      method: "GET",
    }
  );
}

export function adminGetAssistantConversationsByUser({
  user_id,
  whatsapp_number,
  limit = 100,
} = {}) {
  const params = new URLSearchParams();
  if (user_id) params.set("user_id", user_id);
  if (whatsapp_number) params.set("whatsapp_number", whatsapp_number);
  if (limit) params.set("limit", limit);

  const query = params.toString();
  return adminRequest(
    `/api/admin/assistant-conversations/by-user${query ? `?${query}` : ""}`,
    {
      method: "GET",
    }
  );
}

export function adminGetCorporateUsage(school_id) {
  return adminRequest(
    `/api/admin/corporate/usage?school_id=${encodeURIComponent(school_id)}`,
    {
      method: "GET",
    }
  );
}

export function adminUpsertCorporateUsageCap(payload) {
  return adminRequest("/api/admin/corporate/usage/upsert-cap", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminAdjustCorporateUsageUsed(payload) {
  return adminRequest("/api/admin/corporate/usage/adjust-used", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminSetCorporateUsageStatus(payload) {
  return adminRequest("/api/admin/corporate/usage/status", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminUpdateCorporateUsageAdminEmail(payload) {
  return adminRequest("/api/admin/corporate/usage/admin-email", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminGetPublicUsage({ user_id, email, subscription_id } = {}) {
  const params = new URLSearchParams();
  if (user_id) params.set("user_id", user_id);
  if (email) params.set("email", email);
  if (subscription_id) params.set("subscription_id", subscription_id);

  const query = params.toString();
  return adminRequest(`/api/admin/public-usage${query ? `?${query}` : ""}`, {
    method: "GET",
  });
}

export function adminGetCreditEvents({ user_id, email, activity_type, payment_source, limit } = {}) {
  const params = new URLSearchParams();
  if (user_id) params.set("user_id", user_id);
  else if (email) params.set("email", email);
  if (activity_type) params.set("activity_type", activity_type);
  if (payment_source) params.set("payment_source", payment_source);
  if (limit) params.set("limit", limit);
  const query = params.toString();
  return adminRequest(`/api/admin/credit-events${query ? `?${query}` : ""}`, { method: "GET" });
}

export function adminAdjustPublicUsageUsed(payload) {
  return adminRequest("/api/admin/public-usage/adjust-used", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminUpsertPublicUsageCap(payload) {
  return adminRequest("/api/admin/public-usage/upsert-cap", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminSetPublicUsageStatus(payload) {
  return adminRequest("/api/admin/public-usage/status", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminRefreshPublicUsageFromWoo(payload) {
  return adminRequest("/api/admin/public-usage/refresh-from-woo", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminUpsertCorporateTeacher(payload) {
  return adminRequest("/api/admin/corporate/teachers/upsert", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminSetCorporateTeacherStatus(payload) {
  return adminRequest("/api/admin/corporate/teachers/status", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminGetCorporateTeacherByEmail(teacher_email) {
  return adminRequest(
    `/api/admin/corporate/teachers/by-email?teacher_email=${encodeURIComponent(
      teacher_email
    )}`,
    {
      method: "GET",
    }
  );
}

export function adminListCorporateTeachersBySchool(school_id) {
  return adminRequest(
    `/api/admin/corporate/teachers/by-school?school_id=${encodeURIComponent(
      school_id
    )}`,
    {
      method: "GET",
    }
  );
}

export function adminListCorporateSchools() {
  return adminRequest("/api/admin/corporate/schools", {
    method: "GET",
  });
}

export function adminListCorporateSubscriptionProducts() {
  return adminRequest("/api/admin/corporate/subscription-products", {
    method: "GET",
  });
}

export function adminUpsertCorporateSchool(payload) {
  return adminRequest("/api/admin/corporate/schools/upsert", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminAllocateSchoolSubscription(payload) {
  return adminRequest("/api/school-usage/subscription-allocate", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adminListStudyBuddySourcePacks() { return adminRequest("/api/admin/study-buddy/source-packs", { method: "GET" }); }
export function adminGetStudyBuddySourcePack(id) { return adminRequest(`/api/admin/study-buddy/source-packs/${encodeURIComponent(id)}`, { method: "GET" }); }
export function adminListPublishedStudyPacks() { return adminRequest("/api/admin/study-buddy/published-packs", { method: "GET" }); }
export function adminCreatePublishedStudyPack(payload) { return adminRequest("/api/admin/study-buddy/published-packs", { method: "POST", body: JSON.stringify(payload) }); }
export function adminGetPublishedStudyPack(id) { return adminRequest(`/api/admin/study-buddy/published-packs/${encodeURIComponent(id)}`, { method: "GET" }); }
export function adminUpdatePublishedStudyPack(id, payload) { return adminRequest(`/api/admin/study-buddy/published-packs/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload) }); }
